<script lang="ts">
	import { tooltip } from '@astlai/common/tooltip/tooltip.svelte.ts';
	import type { Snippet } from 'svelte';
	import IconOpenInNew from 'virtual:icons/material-symbols/open-in-new-rounded';
	import { getTabNavContext } from './tabs.context.svelte';

	interface Props {
		id: string;
		icon?: Snippet;
		badge?: number | string;
		dot?: boolean;
		external?: boolean;
		href?: string;
		target?: string;
		onclick?: (value: string) => void;
		children: Snippet;
	}
	let { id, icon, badge, dot, external, href, target, children, onclick }: Props = $props();

	const ctx = getTabNavContext();

	function handleClick() {
		onclick?.(id);
		ctx.onchange?.(id);
	}

	let labelEl: HTMLSpanElement;
	let tooltipText = $derived(labelEl?.textContent ?? '');
</script>

{#snippet content()}
	{#if icon}
		<span class="icon">{@render icon()}</span>
	{/if}
	<span class="label" bind:this={labelEl}>{@render children()}</span>
	{#if dot}
		<span class="dot"></span>
	{/if}
	{#if badge != null}
		<span class="badge">{badge}</span>
	{/if}
	{#if external}
		<span class="external-icon"><IconOpenInNew /></span>
	{/if}
{/snippet}

{#if ctx.mode == 'button'}
	<button
		class="tab"
		class:auto-hide-labels={ctx.autoHideLabels}
		class:active={ctx.activeTab === id}
		onclick={() => handleClick()}
		use:tooltip={tooltipText}
	>
		{@render content()}
	</button>
{:else}
	<a
		class="tab"
		class:auto-hide-labels={ctx.autoHideLabels}
		class:active={ctx.activeTab === id}
		href={href ?? `${ctx.baseURL}${id}`}
		{target}
		onclick={() => onclick?.(id)}
		use:tooltip={tooltipText}
	>
		{@render content()}
	</a>
{/if}

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.tab {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
		font-size: 0.875rem;
		color: var(--text-secondary);
		background: none;
		border: none;
		border-radius: 0.5rem;
		cursor: pointer;
		flex: 0 0 auto;
		height: auto;
		overflow: hidden;
		min-width: 0;
		height: auto;

		.icon {
			flex-shrink: 0;
			display: flex;
		}

		.label {
			min-width: 0;
			@include lib.ellipsis();

			color: var(--text-primary);
		}

		.dot {
			flex-shrink: 0;
			width: 6px;
			height: 6px;
			border-radius: 999px;
			background: var(--text-success);
		}

		.badge {
			flex-shrink: 0;
			@include lib.flex-row();
			@include lib.flex-align(center, center);
			min-width: 1rem;
			height: 1rem;
			padding: 0 0.25rem;
			border-radius: 999px;
			font-size: 0.625rem;
			line-height: 0.625rem;
			background: var(--surface-3);
			color: var(--text-secondary);
			font-weight: 600;
		}

		.external-icon {
			flex-shrink: 0;
			display: flex;
			margin-left: auto;
			font-size: 1rem;
		}

		&.active .badge {
			background: var(--surface-1);
			color: var(--text-primary);
		}

		@include lib.bp(gt-xs) {
			flex: 0 1 auto;
		}

		&:hover {
			color: var(--text-primary);
			background: var(--state-hover);
		}

		&.active {
			color: var(--text-primary);
			@include lib.glass-surface;
		}

		&.auto-hide-labels {
			@container (max-width: 380px) {
				flex: 1 1 0;
				.label {
					display: none;
				}
			}
		}
	}
</style>
